import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { getImageUrl, getProfileImageUrlByAccountTypeCode } from "helpers/Image.helper";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { updateCommentLike, addComment, removeCommentById, findCommentById } from "helpers/Comment.helper";
import { getCurrentLangName } from "helpers/String.helper";
import { IDiary, IDiaryComment } from "api/interfaces/profile.interface";
import { getDiaryDetail, toggleDiaryLike, getDiaryComments, createDiaryComment, deleteDiaryComment, toggleCommentLike, translateDiary, ITranslateResponse, translateDiaryComment, ICommentTranslateResponse } from "api/student/v2/diary.api";
import VideoPlayer from "commons/video/VideoPlayer";
import KoachInfoPopup from "./KoachInfoPopup";
import StudentInfoPopup from "commons/student/popup/StudentInfoPopup.container";
import ReportPopup from "commons/popup/ReportPopup";
import LikePopupForDiary from "pages/student/v2/popup/LikePopupForDiary";
import { usePopup } from "contexts/PopupContext";

interface StudentDiaryDetailPopupProps {
    diaryId: string;
    onClose: () => void;
}

const StudentDiaryDetailPopup: React.FC<StudentDiaryDetailPopupProps> = ({ diaryId, onClose }) => {
    // CSS 애니메이션 정의
    const fadeInUpAnimation = `
        @keyframes fadeInUp {
            from {
                opacity: 0;
                transform: translateY(20px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        
        @keyframes fadeIn {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }
    `;
    
    const { info: userInfo } = useSelector((state: any) => state.user);
    const { showPopup } = usePopup();
    const [diary, setDiary] = useState<IDiary | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [commentText, setCommentText] = useState("");
    const [comments, setComments] = useState<IDiaryComment[]>([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentLikeLoading, setCommentLikeLoading] = useState<{ [key: number]: boolean }>({});
    
    // 대댓글 관련 상태
    const [replyToComment, setReplyToComment] = useState<number | null>(null);
    const [replyText, setReplyText] = useState("");
    const [commentSubmitting, setCommentSubmitting] = useState(false);
    
    // 케밥 메뉴 관련 상태
    const [activeKebabMenu, setActiveKebabMenu] = useState<number | null>(null);
    const kebabRef = useRef<HTMLDivElement>(null);
    
    // 신고 관련 상태
    const [showReportPopup, setShowReportPopup] = useState(false);
    const [commentToReport, setCommentToReport] = useState<{ id: number; content: string } | null>(null);

    // 번역 관련 상태
    const [isTranslating, setIsTranslating] = useState(false);
    const [translatedData, setTranslatedData] = useState<ITranslateResponse | null>(null);
    
    // 코멘트 번역 관련 상태
    const [translatingCommentIds, setTranslatingCommentIds] = useState<Set<number>>(new Set());
    const [translatedComments, setTranslatedComments] = useState<Map<number, ICommentTranslateResponse>>(new Map());

    // 코멘트 무한 스크롤을 위한 상태
    const [commentCurrentPage, setCommentCurrentPage] = useState(1);
    const [commentHasMore, setCommentHasMore] = useState(true);
    const [commentLoadingMore, setCommentLoadingMore] = useState(false);
    const [commentsPagination, setCommentsPagination] = useState({ currentPage: 1, hasNextPage: false });

    // 코치 정보 팝업 표시 함수
    const handleKoachInfoPopup = (koachId: string) => {
        const popupContent = (
            <KoachInfoPopup
                koachId={koachId}
                onClose={() => showPopup(null)}
            />
        );
        showPopup(popupContent);
    };

    // 지망생 정보 팝업 표시 함수
    const handleStudentInfoPopup = (studentId: string) => {
        const popupContent = (
            <StudentInfoPopup
                studentId={studentId}
                onClose={() => showPopup(null)}
            />
        );
        showPopup(popupContent);
    };

    // 좋아요 목록 팝업 표시 함수
    const handleShowLikePopup = () => {
        if (diary && diary.likeCount && diary.likeCount > 0) {
            const popupContent = (
                <LikePopupForDiary
                    diaryId={diaryId}
                    onClose={() => showPopup(null)}
                />
            );
            showPopup(popupContent);
        }
    };

    // 케밥 메뉴 토글 함수
    const toggleKebabMenu = (commentId: number) => {
        setActiveKebabMenu(activeKebabMenu === commentId ? null : commentId);
    };

    // 내 코멘트인지 확인 함수
    const isMyComment = (comment: IDiaryComment) => {
        const isDiaryAuthor = diary?.user?.id === userInfo?.id;
        const isCommentAuthor = comment.userId === userInfo?.id;
        return isDiaryAuthor || isCommentAuthor;
    };

    // 댓글 신고 핸들러
    const handleCommentReport = (commentId: number, commentContent: string) => {
        setCommentToReport({ id: commentId, content: commentContent });
        setShowReportPopup(true);
        setActiveKebabMenu(null);
    };

    useEffect(() => {
        fetchDiaryData();
    }, [diaryId]);

    // 케밥 메뉴 외부 클릭 시 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (kebabRef.current && !kebabRef.current.contains(event.target as Node)) {
                setActiveKebabMenu(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const fetchDiaryData = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const result = await getDiaryDetail(diaryId);
            setDiary(result.diary);

            // 다이어리 상세 정보를 성공적으로 가져온 후 코멘트 목록 호출
            await fetchCommentsData(1, false);
        } catch (error) {
            console.error("다이어리 상세 조회 실패:", error);
            setError("다이어리를 불러오는데 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCommentsData = useCallback(async (page: number = 1, append: boolean = false) => {
        console.log('fetchCommentsData called:', { page, append });

        try {
            if (append) {
                setCommentLoadingMore(true);
            } else {
                setCommentsLoading(true);
                setCommentCurrentPage(1);
                setCommentHasMore(true);
            }

            const result = await getDiaryComments(diaryId, { page });
            console.log('API response:', result);

            if (append) {
                // 기존 코멘트에 새 코멘트 추가
                setComments(prevComments => [...prevComments, ...result.commentList]);
            } else {
                // 첫 페이지 로드
                setComments(result.commentList);
            }

            // 페이지네이션 정보 업데이트
            setCommentHasMore(result.pagination.hasNextPage);
            if (!append) {
                setCommentCurrentPage(result.pagination.currentPage);
            }

            // commentsPagination 상태도 업데이트
            setCommentsPagination(prevPagination => {
                const newPagination = {
                    currentPage: result.pagination.currentPage,
                    hasNextPage: result.pagination.hasNextPage
                };
                console.log('Pagination updated:', newPagination);
                return newPagination;
            });

        } catch (error) {
            console.error("코멘트 목록 조회 실패:", error);
            if (!append) {
                setComments([]);
                setCommentHasMore(false);
            }
        } finally {
            if (append) {
                setCommentLoadingMore(false);
            } else {
                setCommentsLoading(false);
            }
        }
    }, [diaryId]);



    // 팝업 내부 스크롤 이벤트 감지하여 코멘트 무한 스크롤
    useEffect(() => {
        let isScrolling = false; // 스크롤 중복 방지 플래그

        const handleScroll = (event: Event) => {
            const target = event.target as HTMLElement;
            if (!target) return;

            // 이미 스크롤 처리 중이면 무시
            if (isScrolling) return;

            const scrollTop = target.scrollTop;
            const scrollHeight = target.scrollHeight;
            const clientHeight = target.clientHeight;

            // 디버깅을 위한 로그
            console.log('Scroll Event:', {
                scrollTop,
                scrollHeight,
                clientHeight,
                threshold: scrollTop + clientHeight,
                target: scrollHeight - 100,
                isLoadingMore: commentLoadingMore,
                hasNextPage: commentsPagination.hasNextPage
            });

            // 스크롤이 하단 근처에 도달했는지 확인 (100px 여유)
            if (scrollTop + clientHeight >= scrollHeight - 100) {
                console.log('Scroll threshold reached!');

                if (!commentLoadingMore && commentsPagination.hasNextPage) {
                    console.log('Loading next page...');
                    isScrolling = true; // 스크롤 처리 시작

                    const nextPage = commentsPagination.currentPage + 1;
                    fetchCommentsData(nextPage, true).finally(() => {
                        // API 호출 완료 후 플래그 리셋 (1초 후)
                        setTimeout(() => {
                            isScrolling = false;
                        }, 1000);
                    });
                } else {
                    console.log('Cannot load next page:', {
                        isLoadingMore: commentLoadingMore,
                        hasNextPage: commentsPagination.hasNextPage
                    });
                }
            }
        };

        // 팝업의 스크롤 가능한 요소를 찾아서 이벤트 리스너 추가
        const popupElement = document.querySelector('.overflow-y-auto');
        if (popupElement) {
            console.log('Scroll event listener added to:', popupElement);
            popupElement.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                popupElement.removeEventListener('scroll', handleScroll);
            };
        } else {
            console.log('Popup element with .overflow-y-auto not found');
        }
    }, [commentsPagination.hasNextPage, commentLoadingMore, commentsPagination.currentPage]); // 필요한 의존성 추가

    const handleClose = () => {
        onClose();
    };

    const handleImageClick = (imageUrl: string) => {
        // 이미지 클릭 시 확대 보기 (필요시 구현)
        window.open(imageUrl, '_blank');
    };

    const handleVideoClick = (videoUrl: string) => {
        // 비디오 클릭 시 새 창에서 재생 (필요시 구현)
        window.open(videoUrl, '_blank');
    };

    const handleLikeToggle = async () => {
        try {
            const result = await toggleDiaryLike(diaryId);

            // 다이어리 데이터 업데이트
            setDiary(prevDiary => {
                if (!prevDiary) return prevDiary;
                return {
                    ...prevDiary,
                    isLiked: result.isLiked,
                    likeCount: result.likeCount
                };
            });

        } catch (error) {
            console.error("좋아요 토글 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        }
    };

    const handleCommentSubmit = async () => {
        if (!commentText.trim() || commentSubmitting) return;

        try {
            setCommentSubmitting(true);
            // 코멘트 작성 API 호출
            await createDiaryComment(diaryId, commentText.trim());

            // 입력란 초기화
            setCommentText("");

            // 코멘트 목록 새로고침
            await fetchCommentsData(1, false);
        } catch (error) {
            console.error("코멘트 작성 실패:", error);
            // TODO: 에러 메시지 표시 (팝업 또는 토스트)
        } finally {
            setCommentSubmitting(false);
        }
    };

    // 대댓글 작성 핸들러
    const handleReplyClick = (commentId: number) => {
        setReplyToComment(commentId);
        setReplyText("");
    };

    // 대댓글 취소 핸들러
    const handleCancelReply = () => {
        setReplyToComment(null);
        setReplyText("");
    };

    // 대댓글 제출 핸들러
    const handleReplySubmit = async () => {
        if (!replyText.trim() || !replyToComment || !diaryId || commentSubmitting) return;

        try {
            setCommentSubmitting(true);
            const result = await createDiaryComment(diaryId, replyText.trim(), replyToComment);

            if (result && result.comment) {
                // 헬퍼 함수를 사용하여 대댓글 추가
                setComments(prevComments => 
                    addComment(prevComments, result.comment, replyToComment)
                );

                // 해당 부모 댓글 위치로 스크롤
                setTimeout(() => {
                    const commentElement = document.getElementById(`comment-${replyToComment}`);
                    if (commentElement) {
                        commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 100);

                // 성공 시 상태 초기화
                setReplyToComment(null);
                setReplyText("");
            }
        } catch (error) {
            console.error("대댓글 작성 실패:", error);
        } finally {
            setCommentSubmitting(false);
        }
    };

    const handleCommentLikeToggle = async (commentId: number) => {
        try {
            setCommentLikeLoading(prev => ({ ...prev, [commentId]: true }));
            console.log("❤️ 댓글 좋아요 토글 시작:", commentId);

            const result = await toggleCommentLike(diaryId, commentId);
            console.log("✅ 댓글 좋아요 토글 결과:", { commentId, result });

            // 헬퍼 함수를 사용하여 부모/자식 댓글 모두에서 해당 commentId 찾아서 업데이트
            setComments(prevComments =>
                updateCommentLike(prevComments, commentId, {
                    isLiked: result.isLiked,
                    likeCount: result.likeCount
                })
            );
        } catch (error) {
            console.error("❌ 댓글 좋아요 토글 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            setCommentLikeLoading(prev => ({ ...prev, [commentId]: false }));
        }
    };

    // 번역 처리
    const handleTranslate = async () => {
        if (isTranslating || translatedData) {
            console.log("🚫 이미 번역 처리 중이거나 번역된 다이어리:", diaryId);
            return;
        }

        try {
            setIsTranslating(true);
            console.log("🌐 번역 시작:", diaryId);

            const result = await translateDiary(diaryId);
            console.log("번역 결과:", result);

            setTranslatedData(result);

        } catch (error) {
            console.error("번역 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            setIsTranslating(false);
            console.log("✅ 번역 완료:", diaryId);
        }
    };

    // 코멘트 번역 처리
    const handleCommentTranslate = async (commentId: number) => {
        if (translatingCommentIds.has(commentId) || translatedComments.has(commentId)) {
            console.log("🚫 이미 번역 처리 중이거나 번역된 코멘트:", commentId);
            return;
        }

        try {
            setTranslatingCommentIds(prev => new Set(prev).add(commentId));
            console.log("🌐 코멘트 번역 시작:", commentId);

            const result = await translateDiaryComment(diaryId, commentId.toString());
            console.log("코멘트 번역 결과:", result);

            setTranslatedComments(prev => new Map(prev).set(commentId, result));

        } catch (error) {
            console.error("코멘트 번역 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            setTranslatingCommentIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(commentId);
                return newSet;
            });
            console.log("✅ 코멘트 번역 완료:", commentId);
        }
    };

    // 코멘트 삭제 (부모/자식 댓글 모두 처리)
    const handleCommentDelete = async (commentId: number) => {
        if (!diaryId) return;

        // 헬퍼 함수를 사용하여 부모/자식 댓글에서 해당 commentId 찾기
        const comment = findCommentById(comments, commentId);
        if (!comment) {
            console.error("삭제할 댓글을 찾을 수 없음:", commentId);
            return;
        }

        // 내가 작성한 댓글인지 확인
        if (comment.userId !== userInfo?.id) {
            alert("자신이 작성한 댓글만 삭제할 수 있습니다.");
            return;
        }

        const isConfirmed = window.confirm("정말로 이 댓글을 삭제하시겠습니까?");
        if (isConfirmed) {
            try {
                console.log("🗑️ 댓글 삭제 시작:", commentId);
                await deleteDiaryComment(diaryId, commentId);
                
                // 헬퍼 함수를 사용하여 부모/자식 댓글에서 해당 commentId 제거
                setComments(prevComments => removeCommentById(prevComments, commentId));
                console.log("✅ 댓글 삭제 완료:", commentId);
            } catch (error) {
                console.error("❌ 댓글 삭제 실패:", error);
                alert("댓글 삭제에 실패했습니다. 다시 시도해주세요.");
            }
        }
    };

    // 로딩 상태
    if (isLoading) {
        return (
            <>
                <style>{fadeInUpAnimation}</style>
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                    style={{
                        animation: 'fadeIn 0.4s ease-out forwards',
                        opacity: 0
                    }}
                >
                    <div 
                        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
                        style={{
                            animation: 'fadeInUp 0.4s ease-out forwards',
                            transform: 'translateY(20px)',
                            opacity: 0
                        }}
                    >
                    <div className="animate-pulse p-6">
                        {/* 헤더 스켈레톤 */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                                <div>
                                    <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                                </div>
                            </div>
                            <div className="w-6 h-6 bg-gray-200 rounded"></div>
                        </div>

                        {/* 제목 스켈레톤 */}
                        <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>

                        {/* 내용 스켈레톤 */}
                        <div className="space-y-3 mb-6">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                        </div>

                        {/* 미디어 스켈레톤 */}
                        <div className="space-y-4">
                            {/* 비디오 스켈레톤 */}
                            <div>
                                <div className="h-4 bg-gray-200 rounded w-16 mb-3"></div>
                                <div className="h-48 bg-gray-200 rounded-lg"></div>
                            </div>

                            {/* 이미지 스켈레톤 */}
                            <div>
                                <div className="h-4 bg-gray-200 rounded w-16 mb-3"></div>
                                <div className="flex gap-3">
                                    <div className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                    <div className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                    <div className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                </div>
                            </div>
                        </div>

                        {/* 통계 정보 스켈레톤 */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-6">
                            <div className="flex items-center space-x-4">
                                <div className="h-4 bg-gray-200 rounded w-8"></div>
                                <div className="h-4 bg-gray-200 rounded w-8"></div>
                            </div>
                            <div className="h-3 bg-gray-200 rounded w-24"></div>
                        </div>
                    </div>
                </div>
            </div>
        </>
        );
    }

    // 에러 상태
    if (error || !diary) {
        return (
            <>
                <style>{fadeInUpAnimation}</style>
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                    style={{
                        animation: 'fadeIn 0.4s ease-out forwards',
                        opacity: 0
                    }}
                >
                    <div 
                        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 p-6"
                        style={{
                            animation: 'fadeInUp 0.4s ease-out forwards',
                            transform: 'translateY(20px)',
                            opacity: 0
                        }}
                    >
                    <div className="text-center">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.txtLoadError") || "다이어리 로드 실패"}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {error || "다이어리를 불러올 수 없습니다."}
                        </p>
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            {getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.btnClose") || "닫기"}
                        </button>
                    </div>
                </div>
            </div>
        </>
        );
    }

    return (
        <>
            <style>{fadeInUpAnimation}</style>
            <div 
                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                style={{
                    animation: 'fadeIn 0.4s ease-out forwards',
                    opacity: 0
                }}
            >
                <div 
                    className="popup-content bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
                    style={{
                        animation: 'fadeInUp 0.4s ease-out forwards',
                        transform: 'translateY(20px)',
                        opacity: 0
                    }}
                >
                {/* 헤더 - 스크롤해도 항상 상단에 고정 */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between py-2 px-4">
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 rounded-full overflow-hidden">
                                <img
                                    src={getImageUrl(diary.user.userStudent.profileImage) || "/imgs/dummy/basic-img.png"}
                                    alt={diary.user.userStudent.nickname}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">
                                    {diary.user.userStudent.nickname}
                                </h3>
                                <p className="text-xs text-gray-500">
                                    {formatTimeAgo(new Date(diary.createdAt))}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            {/* 번역 버튼 */}
                            <button
                                className={`flex items-center space-x-1 px-2 py-1 rounded-lg transition-all duration-200 ${
                                    isTranslating
                                        ? 'opacity-50 cursor-not-allowed bg-gray-100'
                                        : translatedData
                                        ? 'bg-[#CF3688]/10 text-[#CF3688] hover:bg-[#CF3688]/20'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                                onClick={handleTranslate}
                                disabled={isTranslating}
                            >
                                {isTranslating ? (
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    <img 
                                        src={translatedData ? "/imgs/icons/icon-translate-active.svg" : "/imgs/icons/icon-translate.svg"} 
                                        alt="translate" 
                                        className="w-4 h-4" 
                                    />
                                )}
                            </button>
                            <button
                                onClick={handleClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* 다이어리 내용 */}
                <div className="p-6 pb-24">
                    {/* 제목과 포트폴리오 여부 */}
                    <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                            <h2 
                                className="text-xl font-bold text-gray-900"
                                style={{
                                    wordBreak: 'break-all',
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'normal'
                                }}
                            >
                                {diary.title}
                            </h2>
                            {diary.isPortfolio && (
                                <div className="flex items-center gap-1 px-3 py-1 bg-[#CF3688]/10 text-[#CF3688] rounded-full text-sm font-medium">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    포트폴리오
                                </div>
                            )}
                        </div>
                        {translatedData && (
                            <h2 
                                className="text-xl font-bold text-gray-500 mt-2 pl-3 border-l-2 border-gray-400"
                                style={{
                                    wordBreak: 'break-all',
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'normal'
                                }}
                            >
                                {translatedData.translatedTitle}
                            </h2>
                        )}
                    </div>

                    {/* 내용 */}
                    <div className="mb-6">
                        <div 
                            className="text-gray-700"
                            style={{
                                wordBreak: 'break-all',
                                overflowWrap: 'anywhere',
                                whiteSpace: 'pre-wrap'
                            }}
                        >
                            {diary.content}
                        </div>
                        {translatedData && (
                            <div 
                                className="text-gray-500 mt-2 pl-3 border-l-2 border-gray-400"
                                style={{
                                    wordBreak: 'break-all',
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'pre-wrap'
                                }}
                            >
                                {translatedData.translatedContent}
                            </div>
                        )}
                    </div>

                    {/* 비디오들 (우선 출력) */}
                    {diary.diaryVideos && diary.diaryVideos.length > 0 && (
                        <div className="mb-6">
                            <div className="space-y-3">
                                {diary.diaryVideos.map((diaryVideo) => {
                                    return (
                                        <VideoPlayer
                                            video={diaryVideo.video}
                                            className="w-full h-full object-cover"
                                            onVideoEnd={() => {
                                                // 비디오 재생 종료 시 콜백 (필요시 구현)
                                            }}
                                            onVideoError={(error) => {
                                                console.error('비디오 에러:', error);
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* 이미지들 (좌우 스크롤) */}
                    {diary.diaryImages && diary.diaryImages.length > 0 && (
                        <div className="mb-6">
                            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                {diary.diaryImages.map((diaryImage) => (
                                    <div
                                        key={diaryImage.id}
                                        className="flex-shrink-0 aspect-square w-32 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                                        onClick={() => handleImageClick(getImageUrl(diaryImage.image))}
                                    >
                                        <img
                                            src={getImageUrl(diaryImage.image)}
                                            alt={`다이어리 이미지 ${diaryImage.sequence}`}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 통계 정보 */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                            {/* 좋아요 버튼 */}
                            <button
                                className="flex items-center space-x-1 hover:opacity-80 transition-opacity"
                                onClick={handleLikeToggle}
                            >
                                <svg
                                    className={`w-4 h-4 ${diary.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}`}
                                    fill={diary.isLiked ? '#CF3688' : 'none'}
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                                <span className={diary.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}>
                                    {diary.likeCount || 0}
                                </span>
                            </button>

                            {/* 댓글 수 */}
                            {diary.commentCount !== undefined && (
                                <div className="flex items-center space-x-1">
                                    <img src="/imgs/icons/icon-comment.svg" alt="댓글" className="w-4 h-4" />
                                    <span>{diary.commentCount}</span>
                                </div>
                            )}
                            
                            {/* 북마크 수 */}
                            <div className="flex items-center space-x-1 text-gray-500">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                </svg>
                                <span className="text-xs font-medium">{(diary as any).bookmarkCount || 0}</span>
                            </div>
                        </div>
                    </div>

                    {/* 활동영역 - 좋아요한 유저들 */}
                    {diary.likes && diary.likes.length > 0 && (
                        <div className="pt-3 border-t border-gray-100 mb-6">
                            <div
                                className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 py-1.5 rounded-lg transition-colors"
                                onClick={handleShowLikePopup}
                            >
                                {/* 좋아요한 유저들의 프로필 사진 */}
                                <div className="flex -space-x-2">
                                    {diary.likes.slice(0, 3).map((like, index) => (
                                        <div
                                            key={like.userId}
                                            className="w-6 h-6 rounded-full border-2 border-white overflow-hidden shadow-sm"
                                            style={{ zIndex: 5 - index }}
                                        >
                                            <img
                                                src={getProfileImageUrlByAccountTypeCode(like?.user, 32, 32) || "/imgs/dummy/basic-img.png"}
                                                alt={like?.user?.userStudent?.nickname || like?.user?.username || "User"}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = "/imgs/dummy/basic-img.png";
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* 좋아요 텍스트 - 우선순위별 조건 */}
                                {diary.castingManagerLikeCount !== undefined && diary.castingManagerLikeCount > 0 ? (
                                    /* 1순위: 캐스팅 매니저 좋아요가 있는 경우 */
                                    <div className="flex items-center space-x-1 text-xs text-gray-600">
                                        <span className="text-gray-500">
                                            {diary.castingManagerLikeCount === 1 
                                                ? getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtCastingManagerLikeSingle")
                                                : getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtCastingManagerLikeMultiple")
                                            }
                                        </span>
                                    </div>
                                ) : (
                                    /* 2순위: 캐스팅 매니저 좋아요가 없고, 일반 좋아요가 3개 초과인 경우 */
                                    diary.likeCount > 3 && (
                                        <div className="flex items-center space-x-1 text-xs text-gray-600">
                                            <span className="text-gray-500">
                                                {getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtOthersLike")}
                                            </span>
                                        </div>
                                    )
                                )}

                                {/* 화살표 아이콘 */}
                                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </div>
                        </div>
                    )}

                    {/* 코멘트 섹션 */}
                    <div className="border-t border-gray-200 pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            {getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.txtComments")}
                        </h3>

                        {/* 코멘트 목록 */}
                        <div className="space-y-1.5">
                            {commentsLoading ? (
                                // 코멘트 스켈레톤 UI
                                <>
                                    {[1, 2, 3].map((index) => (
                                        <div key={`skeleton-${index}`} className="border border-gray-200 rounded-lg p-3 animate-pulse">
                                            {/* 프로필 이미지 스켈레톤 */}
                                            <div className="flex items-center space-x-2 mb-1.5">
                                                <div className="w-7 h-7 bg-gray-300 rounded-full"></div>
                                                <div className="flex flex-col space-y-1.5">
                                                    <div className="h-4 bg-gray-300 rounded w-24"></div>
                                                    <div className="h-3 bg-gray-300 rounded w-16"></div>
                                                </div>
                                            </div>

                                            {/* 코멘트 내용 스켈레톤 */}
                                            <div className="space-y-2 mb-2">
                                                <div className="h-4 bg-gray-300 rounded w-full"></div>
                                                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                                            </div>

                                            {/* 좋아요 버튼 스켈레톤 */}
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 bg-gray-300 rounded"></div>
                                                    <div className="h-3 bg-gray-300 rounded w-8"></div>
                                                </div>
                                                <div className="h-3 bg-gray-300 rounded w-16"></div>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            ) : comments.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.txtNoComments") || "아직 작성된 코멘트가 없습니다."}
                                </div>
                            ) : (
                                comments.map((comment) => (
                                    <div key={comment.id} id={`comment-${comment.id}`} className="border border-gray-200 rounded-lg p-3">
                                        {/* 코멘트 헤더 */}
                                        <div className="flex items-center space-x-2 mb-1.5">
                                            {/* 프로필 이미지/로고 */}
                                            <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0">
                                                {comment.user?.userStudent && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (comment.user?.userStudent?.id) {
                                                                handleStudentInfoPopup(comment.user.userStudent.id.toString());
                                                            }
                                                        }}
                                                        className="w-full h-full hover:opacity-80 transition-opacity cursor-pointer"
                                                    >
                                                        <img
                                                            src={getImageUrl(comment.user?.userStudent?.profileImage) || "/imgs/dummy/basic-img.png"}
                                                            alt="프로필"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </button>
                                                )}
                                                {comment.user?.userKoach && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (comment.user?.userKoach?.id) {
                                                                handleKoachInfoPopup(comment.user.userKoach.id.toString());
                                                            }
                                                        }}
                                                        className="w-full h-full hover:opacity-80 transition-opacity cursor-pointer"
                                                    >
                                                        <img
                                                            src={getImageUrl(comment.user?.userKoach?.profileImage) || "/imgs/dummy/basic-img.png"}
                                                            alt="프로필"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </button>
                                                )}
                                                {comment.user?.userSchool && (
                                                    <img
                                                        src={getImageUrl(comment.user?.userSchool?.logoImageId) || "/imgs/dummy/school-logo/school-logo-1.png"}
                                                        alt="학원 로고"
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                                {comment.user?.userAgency && (
                                                    <img
                                                        src={getImageUrl(comment.user?.userAgency?.agency.logoImage) || "/imgs/dummy/agency-logo/agency-logo-1.png"}
                                                        alt="기획사 로고"
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                            </div>

                                            {/* 사용자 정보 */}
                                            <div className="flex flex-col flex-1">
                                                <span className="font-medium text-gray-900 text-sm">
                                                    {comment.user?.userStudent && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (comment.user?.userStudent?.id) {
                                                                    handleStudentInfoPopup(comment.user.userStudent.id.toString());
                                                                }
                                                            }}
                                                            className="hover:text-[#CF3688] hover:underline cursor-pointer transition-colors"
                                                        >
                                                            {comment.user.userStudent.nickname ||
                                                                `${comment.user.userStudent.firstName} ${comment.user.userStudent.lastName}` ||
                                                                "알 수 없음"}
                                                        </button>
                                                    )}
                                                    {comment.user?.userKoach && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (comment.user?.userKoach?.id) {
                                                                    handleKoachInfoPopup(comment.user.userKoach.id.toString());
                                                                }
                                                            }}
                                                            className="hover:text-[#CF3688] hover:underline cursor-pointer transition-colors"
                                                        >
                                                            {comment.user.userKoach.nickname ||
                                                                `${comment.user.userKoach.firstName} ${comment.user.userKoach.lastName}` ||
                                                                "알 수 없음"}
                                                        </button>
                                                    )}
                                                    {comment.user?.userSchool && (
                                                        comment.user.userSchool.schoolName || "알 수 없음"
                                                    )}
                                                    {comment.user?.userAgency && (
                                                        comment.user.userAgency.name || "알 수 없음"
                                                    )}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    {getCurrentLangName(comment.user?.accountType?.names) || comment.user?.accountTypeCode}
                                                </span>
                                            </div>

                                            {/* 번역 버튼과 케밥 버튼 */}
                                            <div className="relative flex-shrink-0 flex items-center space-x-1">
                                                {/* 번역 버튼 */}
                                                <button
                                                    className={`p-1 rounded-full transition-all duration-200 ${
                                                        translatingCommentIds.has(comment.id)
                                                            ? 'opacity-50 cursor-not-allowed'
                                                            : 'hover:bg-gray-100 cursor-pointer'
                                                    }`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!translatingCommentIds.has(comment.id)) {
                                                            handleCommentTranslate(comment.id);
                                                        }
                                                    }}
                                                    disabled={translatingCommentIds.has(comment.id)}
                                                >
                                                    {translatingCommentIds.has(comment.id) ? (
                                                        <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    ) : (
                                                        <img 
                                                            src={translatedComments.get(comment.id) ? "/imgs/icons/icon-translate-active.svg" : "/imgs/icons/icon-translate.svg"} 
                                                            alt="translate" 
                                                            className="w-4 h-4" 
                                                        />
                                                    )}
                                                </button>

                                                {/* 케밥 버튼 */}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleKebabMenu(comment.id);
                                                    }}
                                                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                                >
                                                    <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                                    </svg>
                                                </button>

                                                {/* 케밥 메뉴 툴팁 */}
                                                {activeKebabMenu === comment.id && (
                                                    <div
                                                        ref={kebabRef}
                                                        className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]"
                                                    >
                                                        {isMyComment(comment) ? (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleCommentDelete(comment.id);
                                                                    setActiveKebabMenu(null);
                                                                }}
                                                                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                            >
                                                                삭제
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleCommentReport(comment.id, comment.contents);
                                                                }}
                                                                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                            >
                                                                신고
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* 코멘트 내용 */}
                                        <div className="mb-2">
                                            <p 
                                                className="text-gray-700"
                                                style={{
                                                    wordBreak: 'break-all',
                                                    overflowWrap: 'anywhere',
                                                    whiteSpace: 'normal'
                                                }}
                                            >
                                                {comment.contents}
                                            </p>
                                            {translatedComments.get(comment.id) && (
                                                <p 
                                                    className="text-gray-500 mt-1 pl-3 border-l-2 border-gray-400"
                                                    style={{
                                                        wordBreak: 'break-all',
                                                        overflowWrap: 'anywhere',
                                                        whiteSpace: 'normal'
                                                    }}
                                                >
                                                    {translatedComments.get(comment.id)?.translatedContents}
                                                </p>
                                            )}
                                        </div>

                                        {/* 코멘트 좋아요, 대댓글, 삭제 버튼 및 작성시간 */}
                                        <div className="flex items-center justify-between">
                                            {/* 좌측: 좋아요 버튼과 대댓글 버튼 */}
                                            <div className="flex items-center gap-3">
                                                {/* 좋아요 버튼 */}
                                                <button
                                                    className={`flex items-center space-x-1 transition-opacity ${commentLikeLoading[comment.id]
                                                            ? 'opacity-50 cursor-not-allowed'
                                                            : 'hover:opacity-80 cursor-pointer'
                                                        }`}
                                                    onClick={() => handleCommentLikeToggle(comment.id)}
                                                    disabled={commentLikeLoading[comment.id]}
                                                >
                                                    {commentLikeLoading[comment.id] ? (
                                                        <svg className="w-4 h-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    ) : (
                                                        <svg
                                                            className={`w-4 h-4 ${comment.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}`}
                                                            fill={comment.isLiked ? '#CF3688' : 'none'}
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                        </svg>
                                                    )}
                                                    <span className={`text-xs ${comment.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}`}>
                                                        {comment.likeCount || 0}
                                                    </span>
                                                </button>

                                                {/* 대댓글 버튼 */}
                                                <button
                                                    className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                                                    onClick={() => handleReplyClick(comment.id)}
                                                >
                                                    <img src="/imgs/icons/icon-reply.svg" alt="대댓글" className="w-4 h-4" />
                                                </button>
                                            </div>

                                            {/* 우측: 작성시간 */}
                                            <div className="flex items-center gap-2">
                                                {/* 작성시간 */}
                                                <span className="text-xs text-gray-400">
                                                    {formatTimeAgo(comment.createdAt)}
                                                </span>
                                            </div>
                                        </div>


                                        {/* 대댓글 목록 */}
                                        {comment.childList && comment.childList.length > 0 && (
                                            <div className="mt-3 ml-4 space-y-2">
                                                {comment.childList.map((reply: IDiaryComment) => (
                                                    <div key={reply.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                                        {/* 대댓글 헤더 */}
                                                        <div className="flex items-center space-x-2 mb-1.5">
                                                            {/* 프로필 이미지/로고 */}
                                                            <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                                                                {reply.user?.userStudent && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (reply.user?.userStudent?.id) {
                                                                                handleStudentInfoPopup(reply.user.userStudent.id.toString());
                                                                            }
                                                                        }}
                                                                        className="w-full h-full hover:opacity-80 transition-opacity cursor-pointer"
                                                                    >
                                                                        <img
                                                                            src={getImageUrl(reply.user?.userStudent?.profileImage) || "/imgs/dummy/basic-img.png"}
                                                                            alt="프로필"
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                    </button>
                                                                )}
                                                                {reply.user?.userKoach && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (reply.user?.userKoach?.id) {
                                                                                handleKoachInfoPopup(reply.user.userKoach.id.toString());
                                                                            }
                                                                        }}
                                                                        className="w-full h-full hover:opacity-80 transition-opacity cursor-pointer"
                                                                    >
                                                                        <img
                                                                            src={getImageUrl(reply.user?.userKoach?.profileImage) || "/imgs/dummy/basic-img.png"}
                                                                            alt="프로필"
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                    </button>
                                                                )}
                                                                {reply.user?.userSchool && (
                                                                    <img
                                                                        src={getImageUrl(reply.user?.userSchool?.logoImageId) || "/imgs/dummy/school-logo/school-logo-1.png"}
                                                                        alt="학원 로고"
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                )}
                                                                {reply.user?.userAgency && (
                                                                    <img
                                                                        src={getImageUrl(reply.user?.userAgency?.agency.logoImage) || "/imgs/dummy/agency-logo/agency-logo-1.png"}
                                                                        alt="기획사 로고"
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                )}
                                                            </div>

                                                            {/* 사용자 정보 */}
                                                            <div className="flex flex-col flex-1">
                                                                <span className="font-medium text-gray-900 text-xs">
                                                                    {reply.user?.userStudent && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                if (reply.user?.userStudent?.id) {
                                                                                    handleStudentInfoPopup(reply.user.userStudent.id.toString());
                                                                                }
                                                                            }}
                                                                            className="hover:text-[#CF3688] hover:underline cursor-pointer transition-colors"
                                                                        >
                                                                            {reply.user.userStudent.nickname ||
                                                                                `${reply.user.userStudent.firstName} ${reply.user.userStudent.lastName}` ||
                                                                                "알 수 없음"}
                                                                        </button>
                                                                    )}
                                                                    {reply.user?.userKoach && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                if (reply.user?.userKoach?.id) {
                                                                                    handleKoachInfoPopup(reply.user.userKoach.id.toString());
                                                                                }
                                                                            }}
                                                                            className="hover:text-[#CF3688] hover:underline cursor-pointer transition-colors"
                                                                        >
                                                                            {reply.user.userKoach.nickname ||
                                                                                `${reply.user.userKoach.firstName} ${reply.user.userKoach.lastName}` ||
                                                                                "알 수 없음"}
                                                                        </button>
                                                                    )}
                                                                    {reply.user?.userSchool && (
                                                                        reply.user.userSchool.schoolName || "알 수 없음"
                                                                    )}
                                                                    {reply.user?.userAgency && (
                                                                        reply.user.userAgency.name || "알 수 없음"
                                                                    )}
                                                                </span>
                                                                <span className="text-xs text-gray-500">
                                                                    {getCurrentLangName(reply.user?.accountType?.names) || reply.user?.accountTypeCode}
                                                                </span>
                                                            </div>

                                                            {/* 번역 버튼과 케밥 버튼 */}
                                                            <div className="relative flex-shrink-0 flex items-center space-x-1">
                                                                {/* 번역 버튼 */}
                                                                <button
                                                                    className={`p-1 rounded-full transition-all duration-200 ${
                                                                        translatingCommentIds.has(reply.id)
                                                                            ? 'opacity-50 cursor-not-allowed'
                                                                            : 'hover:bg-gray-100 cursor-pointer'
                                                                    }`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (!translatingCommentIds.has(reply.id)) {
                                                                            handleCommentTranslate(reply.id);
                                                                        }
                                                                    }}
                                                                    disabled={translatingCommentIds.has(reply.id)}
                                                                >
                                                                    {translatingCommentIds.has(reply.id) ? (
                                                                        <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                    ) : (
                                                                        <img 
                                                                            src={translatedComments.get(reply.id) ? "/imgs/icons/icon-translate-active.svg" : "/imgs/icons/icon-translate.svg"} 
                                                                            alt="translate" 
                                                                            className="w-4 h-4" 
                                                                        />
                                                                    )}
                                                                </button>

                                                                {/* 케밥 버튼 */}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleKebabMenu(reply.id);
                                                                    }}
                                                                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                                                >
                                                                    <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                                                                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                                                    </svg>
                                                                </button>

                                                                {/* 케밥 메뉴 툴팁 */}
                                                                {activeKebabMenu === reply.id && (
                                                                    <div
                                                                        ref={kebabRef}
                                                                        className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]"
                                                                    >
                                                                        {isMyComment(reply) ? (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleCommentDelete(reply.id);
                                                                                    setActiveKebabMenu(null);
                                                                                }}
                                                                                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                                            >
                                                                                삭제
                                                                            </button>
                                                                        ) : (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleCommentReport(reply.id, reply.contents);
                                                                                }}
                                                                                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                                            >
                                                                                신고
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* 대댓글 내용 */}
                                                        <div className="mb-2">
                                                            <p 
                                                                className="text-gray-700 text-sm"
                                                                style={{
                                                                    wordBreak: 'break-all',
                                                                    overflowWrap: 'anywhere',
                                                                    whiteSpace: 'normal'
                                                                }}
                                                            >
                                                                {reply.contents}
                                                            </p>
                                                            {translatedComments.get(reply.id) && (
                                                                <p 
                                                                    className="text-gray-500 text-sm mt-1 pl-3 border-l-2 border-gray-400"
                                                                    style={{
                                                                        wordBreak: 'break-all',
                                                                        overflowWrap: 'anywhere',
                                                                        whiteSpace: 'normal'
                                                                    }}
                                                                >
                                                                    {translatedComments.get(reply.id)?.translatedContents}
                                                                </p>
                                                            )}
                                                        </div>

                                                        {/* 대댓글 좋아요, 삭제 버튼 및 작성시간 */}
                                                        <div className="flex items-center justify-between">
                                                            {/* 좌측: 좋아요 버튼 */}
                                                            <div className="flex items-center gap-3">
                                                                <button
                                                                    className={`flex items-center space-x-1 transition-opacity ${commentLikeLoading[reply.id]
                                                                            ? 'opacity-50 cursor-not-allowed'
                                                                            : 'hover:opacity-80 cursor-pointer'
                                                                        }`}
                                                                    onClick={() => handleCommentLikeToggle(reply.id)}
                                                                    disabled={commentLikeLoading[reply.id]}
                                                                >
                                                                    {commentLikeLoading[reply.id] ? (
                                                                        <svg className="w-4 h-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                    ) : (
                                                                        <svg
                                                                            className={`w-4 h-4 ${reply.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}`}
                                                                            fill={reply.isLiked ? '#CF3688' : 'none'}
                                                                            stroke="currentColor"
                                                                            viewBox="0 0 24 24"
                                                                        >
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                                        </svg>
                                                                    )}
                                                                    <span className={`text-xs ${reply.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}`}>
                                                                        {reply.likeCount || 0}
                                                                    </span>
                                                                </button>
                                                            </div>

                                                            {/* 우측: 작성시간 */}
                                                            <div className="flex items-center gap-2">
                                                                {/* 작성시간 */}
                                                                <span className="text-xs text-gray-400">
                                                                    {formatTimeAgo(reply.createdAt)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}

                            {/* 무한 스크롤 로딩 인디케이터 */}
                            {commentLoadingMore && (
                                <div className="flex justify-center items-center py-4">
                                    <div className="flex items-center space-x-2">
                                        <svg className="w-4 h-4 animate-spin text-[#CF3688]" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span className="text-xs text-gray-500">더 많은 코멘트를 불러오는 중...</span>
                                    </div>
                                </div>
                            )}

                            {/* 더 이상 데이터가 없을 때 표시 */}
                            {!commentHasMore && comments.length > 0 && (
                                <div className="flex justify-center items-center py-4">
                                    <div className="text-center">
                                        <div className="w-8 h-8 mx-auto mb-2 bg-gray-100 rounded-full flex items-center justify-center">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 플로팅 코멘트 입력란 */}
                <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
                    {/* 대댓글 표시 영역 */}
                    {replyToComment && (
                        <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {/* 대댓글 아이콘 */}
                                    <img src="/imgs/icons/icon-reply.svg" alt="대댓글" className="w-4 h-4 text-gray-500" />
                                    
                                    {/* 댓글 내용 */}
                                    <span className="text-sm text-gray-600 truncate max-w-[200px]">
                                        {comments.find(c => c.id === replyToComment)?.contents || ""}
                                    </span>
                                </div>
                                
                                {/* 삭제 버튼 */}
                                <button
                                    onClick={handleCancelReply}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}
                    
                    <div className="flex gap-3">
                        <input
                            type="text"
                            value={replyToComment ? replyText : commentText}
                            onChange={(e) => replyToComment ? setReplyText(e.target.value) : setCommentText(e.target.value)}
                            placeholder={replyToComment
                                ? "대댓글을 입력하세요..."
                                : (getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.txtCommentPlaceholder") || "댓글을 입력하세요...")
                            }
                            maxLength={200}
                            disabled={commentSubmitting}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CF3688] focus:border-[#CF3688] text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            onKeyPress={(e) => e.key === 'Enter' && !commentSubmitting && (replyToComment ? handleReplySubmit() : handleCommentSubmit())}
                        />
                        <button
                            onClick={replyToComment ? handleReplySubmit : handleCommentSubmit}
                            disabled={replyToComment ? !replyText.trim() : !commentText.trim() || commentSubmitting}
                            className="px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#CF3688]/90 focus:outline-none focus:ring-2 focus:ring-[#CF3688] disabled:cursor-not-allowed disabled:opacity-50 transition-colors text-sm font-medium"
                        >
                            {replyToComment ? "답글" : (getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.btnCommentSubmit") || "입력")}
                        </button>
                    </div>
                </div>
            </div>

            {/* 댓글 신고 팝업 */}
            {showReportPopup && commentToReport && (
                <ReportPopup
                    type="comment"
                    diaryId={diaryId}
                    commentId={commentToReport.id.toString()}
                    content={commentToReport.content}
                    onClose={() => {
                        setShowReportPopup(false);
                        setCommentToReport(null);
                    }}
                    onSuccess={() => {
                        // 신고 성공 시 처리 (필요시 추가)
                        console.log('댓글 신고가 완료되었습니다.');
                    }}
                />
            )}
        </div>
        </>
    );
};

export default StudentDiaryDetailPopup;
